import React from 'react';
import { Search, ChevronLeft, BarChart3, Inbox } from 'lucide-react';
import { CandidateProfile, CandidateTotalCountResponse } from '../../Candidate/Types/CandidateType';
import CandidateCardForRecruiter from './CandidateCardForRecruiter';
import LoadMoreButton from '../../Shared/Pagination/LoadMoreButton';
import { useRedirect } from '../../Shared/Hooks/useRedirect ';
import { useParams } from 'react-router-dom';

interface Props {
  searchFilterData: unknown[];
  candidatesTotalStats?: CandidateTotalCountResponse;
  filterStatus?: CandidateProfile[];
  pagination: {
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
    first: boolean;
    last: boolean;
    hasNext: boolean;
    hasPrevious: boolean;
  };
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
}

const STATUS_LABELS: Record<string, string> = {
  all: "All Candidates",
  active: "Actively Exploring",
  new: "Newly Added",
  recently_updated: "Recently Updated",
  today_updated: "Today Updated Profiles",
  me: "Shortlisted By Me",
  pending: "On Hold By Candidates",
  accepted: "Accepted By Candidates",
  rejected: "Rejected By Candidates",
};

const getQuickSearchItems = (stats?: CandidateTotalCountResponse) => [
  { value: "all", label: "All", count: stats?.allCandidates ?? 0 },
  { value: "active", label: "Actively Exploring", count: stats?.activelyExploringCandidates ?? 0 },
  { value: "new", label: "Newly Added", count: stats?.newlyCandidatesBy7days ?? 0 },
  { value: "recently_updated", label: "Recently Updated", count: stats?.recentlyUpdatedCandidatesBy7days ?? 0 },
  { value: "today_updated", label: "Today Updated", count: stats?.todayUpdatedCandidates ?? 0 },
  { value: "me", label: "Shortlisted By Me", count: stats?.invitedByMeCandidates ?? 0 },
  { value: "pending", label: "On Hold By Candidates", count: stats?.invitedPendingByMeCandidates ?? 0 },
  { value: "accepted", label: "Accepted", count: stats?.invitedAcceptedByMeCandidates ?? 0 },
  { value: "rejected", label: "Rejected", count: stats?.invitedRejectedByMeCandidates ?? 0 },
];

export const CandidateSearchWithStats = ({
  candidatesTotalStats,
  filterStatus = [],
  pagination,
  page,
  setPage,
}: Props) => {
  const { status } = useParams();
  const navigate = useRedirect();
  const statusKey = status || "all";
  const quickSearchItems = getQuickSearchItems(candidatesTotalStats);
  const [loading, setLoading] = React.useState(false);

  const handleQuickSearchClick = (value: string) => {
    navigate(`/recruiter/candidates/listing/${value}`);
  };

  return (
    <div className="min-h-screen w-full bg-slate-950 text-slate-100 font-sans antialiased overflow-x-hidden selection:bg-cyan-500/30 selection:text-cyan-200">
      <main className="flex-1 p-3 sm:p-5 lg:p-6 space-y-4 bg-slate-950 max-w-7xl mx-auto">
        {/* ================= HEADER BAND ================= */}
        <div className="rounded-2xl border border-slate-700/50 bg-slate-800/60 p-5 md:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/recruiter/candidates/listing/all")}
                className="p-2 rounded-xl bg-slate-900 border border-slate-700 text-slate-300 hover:text-cyan-400 hover:border-cyan-500/40 transition-all"
                title="Back to all candidates"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div>
                <h1 className="text-lg md:text-xl font-black text-white tracking-tight">
                  {STATUS_LABELS[statusKey] || "Candidate Listing"}
                </h1>
                <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1.5">
                  <Search className="w-3.5 h-3.5 text-cyan-400" />
                  {pagination.totalElements.toLocaleString()} candidates in this segment
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 rounded-xl bg-slate-900/60 border border-slate-700/50 px-4 py-2.5">
              <BarChart3 className="w-4 h-4 text-cyan-400" />
              <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Segment:</span>
              <span className="text-sm font-bold text-cyan-400">
                {pagination.totalElements.toLocaleString()}
              </span>
            </div>
          </div>
        </div>

        {/* ================= QUICK NAV CHIPS ================= */}
        <div className="overflow-x-auto no-scrollbar -mx-1 px-1">
          <div className="flex gap-2 w-max">
            {quickSearchItems.map((item) => {
              const isActive = statusKey === item.value;
              return (
                <button
                  key={item.value}
                  onClick={() => handleQuickSearchClick(item.value)}
                  className={`flex items-center gap-2 whitespace-nowrap px-3.5 py-2 rounded-xl text-xs font-bold border transition-all ${
                    isActive
                      ? "bg-cyan-500/10 text-cyan-400 border-cyan-500/30 shadow-inner"
                      : "bg-slate-800/60 text-slate-400 border-slate-700/50 hover:text-slate-200 hover:border-slate-600"
                  }`}
                >
                  {item.label}
                  <span
                    className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded-md ${
                      isActive ? "bg-cyan-900/40 text-cyan-400" : "bg-slate-900 text-slate-500"
                    }`}
                  >
                    {item.count.toLocaleString()}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ================= RESULTS ================= */}
        <div className="rounded-2xl border border-slate-700/60 bg-slate-800 p-4 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-300">
              {STATUS_LABELS[statusKey] || "Candidates"}
            </h2>
            {filterStatus.length > 0 && (
              <span className="text-xs font-mono font-semibold text-slate-400">
                {filterStatus.length.toLocaleString()} shown of{" "}
                <span className="text-cyan-400 font-bold">{pagination.totalElements.toLocaleString()}</span>
              </span>
            )}
          </div>

          {loading && filterStatus.length === 0 ? (
            <div className="min-h-[220px] flex flex-col justify-center items-center space-y-3">
              <div className="w-10 h-10 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin" />
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest animate-pulse">
                Loading candidates...
              </p>
            </div>
          ) : filterStatus.length === 0 ? (
            <div className="min-h-[220px] flex flex-col justify-center items-center text-center p-6">
              <div className="w-12 h-12 rounded-2xl bg-slate-900 border border-slate-700 flex items-center justify-center mb-3">
                <Inbox className="w-5 h-5 text-slate-500" />
              </div>
              <p className="text-sm font-bold text-slate-300">No candidates in this segment</p>
              <p className="text-xs text-slate-500 mt-1 max-w-xs">
                Try a different segment from the chips above.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filterStatus.map((candidate) => (
                <CandidateCardForRecruiter key={candidate.id} candidate={candidate} />
              ))}

              {filterStatus.length > 0 && (
                <div className="pt-5 border-t border-slate-700/60 mt-6">
                  <LoadMoreButton
                    loading={loading}
                    hasNext={pagination.hasNext}
                    page={page}
                    size={pagination.size}
                    totalElements={pagination.totalElements}
                    currentCount={filterStatus.length}
                    setPage={setPage}
                    variant="dark"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};